import { useState, useCallback, useRef } from "react";
import { User } from "../types/user";
import { identifyUser, resetUser } from "../analytics/posthog";

export function useUser() {
  // This is the state for re-renders
  const [token, setTokenState] = useState<{ [key: string]: string | null }>(() => ({
    accessToken: localStorage.getItem("accessToken"),
    refreshToken: localStorage.getItem("refreshToken"),
  }));
  const [user, setUserState] = useState<User | null>(null);

  // Ref stores the token without causing re-renders 
  const tokenRef = useRef<{ [key: string]: string | null }>({});
  const userRef = useRef<User | null>(null);


  const setUser = useCallback((newUser: User | null) => {
    userRef.current = newUser;
    setUserState(newUser);
    if (newUser) {
      identifyUser(newUser.id, {
        email: newUser.email,
        name: `${newUser.firstName} ${newUser.lastName}`.trim(),
        tier: newUser.subscriptionTier || 'BASIC',
      });
    }
  }, []);


  const clearToken = useCallback(() => {
    tokenRef.current = {};
    setTokenState({});
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    clearToken();
    resetUser();
    console.log('User logged out');
    window.location.href = '/login';
  }, [setUser, clearToken]);


  const setToken = useCallback((tokenName: string, newToken: string | null) => {
    setTokenState(prev => ({ ...prev, [tokenName]: newToken }));
    localStorage.setItem(tokenName, newToken || '');
  }, []);


  return {
    token,
    setToken,
    clearToken,
    getToken: () => tokenRef.current,
    user,
    setUser,
    getUser: () => userRef.current,
    logout
  };
}
